import { sortBy, upperFirst } from 'lodash';
import utilsService from '../../services/utilsService';
import dateService from '../../services/dateService';
import reportsCategoryService from '../../services/reportsCategoryService';
import numberService from '../../services/numberService';
import {
    CLEAN_REPORTS_CATEGORY,
    FETCH_REPORTS_CATEGORY_AMOUNT_FAILURE,
    FETCH_REPORTS_CATEGORY_AMOUNT_REQUEST,
    FETCH_REPORTS_CATEGORY_AMOUNT_SUCCESS,
    FETCH_REPORTS_CATEGORY_CLICKS_FAILURE,
    FETCH_REPORTS_CATEGORY_CLICKS_REQUEST,
    FETCH_REPORTS_CATEGORY_CLICKS_SUCCESS,
    FETCH_REPORTS_CATEGORY_HEATMAP_FAILURE,
    FETCH_REPORTS_CATEGORY_HEATMAP_REQUEST,
    FETCH_REPORTS_CATEGORY_HEATMAP_SUCCESS,
    FETCH_REPORTS_CATEGORY_IMPRESSIONS_FAILURE,
    FETCH_REPORTS_CATEGORY_IMPRESSIONS_REQUEST,
    FETCH_REPORTS_CATEGORY_IMPRESSIONS_SUCCESS,
    FETCH_REPORTS_CATEGORY_QUANTITY_FAILURE,
    FETCH_REPORTS_CATEGORY_QUANTITY_REQUEST,
    FETCH_REPORTS_CATEGORY_QUANTITY_SUCCESS,
    FETCH_REPORTS_CATEGORY_SUMMARY_FAILURE,
    FETCH_REPORTS_CATEGORY_SUMMARY_REQUEST,
    FETCH_REPORTS_CATEGORY_SUMMARY_SUCCESS,
    FETCH_REPORTS_CATEGORY_RANKING_FAILURE,
    FETCH_REPORTS_CATEGORY_RANKING_REQUEST,
    FETCH_REPORTS_CATEGORY_RANKING_SUCCESS,
} from '../mutationTypes';

const getRankingStoreName = rankingType => `ranking${upperFirst(rankingType)}`;

const getHeatmapStoreName = dataType => `heatmap${upperFirst(dataType)}`;

const parseChartData = (data, startDate, endDate) => {
    const chartData = data.chart || [];

    // get uniq chart categories, backend always sends all categories for each row
    const chartCategories = chartData.length > 0
        ? Object.keys(chartData[0]).filter(key => key !== 'date')
        : [];

    let chart = null;

    if (chartCategories && chartCategories.length) {
        // fill missing dates
        const chartMissingDates = dateService.findMissingDates(chartData, startDate, endDate);
        const additionalProperties = chartCategories.map(category => ({ key: category, value: 0 }));
        chart = utilsService.fillArray(chartData, chartMissingDates, 'date', additionalProperties);
        chart = sortBy(chart, ['date']);
    }

    return { chart, chartCategories };
};

const initialState = {
    impressionsChart: {},
    clicksChart: {},
    quantityChart: {},
    amountChart: {},
    heatmapImpressions: {},
    heatmapClicks: {},
    heatmapQuantity: {},
    heatmapAmount: {},
    summary: {},
    rankingImpressions: {},
    rankingClicks: {},
    rankingQuantity: {},
    rankingAmount: {},
    loading: {
        impressionsChart: false,
        clicksChart: false,
        quantityChart: false,
        amountChart: false,
        heatmap: false,
        summary: false,
        ranking: false,
    },
};

const getters = {
    getImpressionsChart: state => (startDate, endDate, campaign, category) => {
        const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
        return state.impressionsChart[storageKey];
    },

    getClicksChart: state => (startDate, endDate, campaign, category) => {
        const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
        return state.clicksChart[storageKey];
    },

    getQuantityChart: state => (startDate, endDate, campaign, category) => {
        const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
        return state.quantityChart[storageKey];
    },

    getAmountChart: state => (startDate, endDate, campaign, category) => {
        const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
        return state.amountChart[storageKey];
    },

    getHeatmap: state => (startDate, endDate, dataType) => {
        const heatmapStoreName = getHeatmapStoreName(dataType);
        const storageKey = utilsService.getStorageKey(startDate, endDate);
        return state[heatmapStoreName][storageKey];
    },

    getSummary: state => (startDate, endDate, compareStartDate, compareEndDate, campaign, category) => {
        const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign, ...category);
        return state.summary[storageKey];
    },

    getRanking: state => (startDate, endDate, compareStartDate, compareEndDate, rankingType, campaign) => {
        const rankingStoreName = getRankingStoreName(rankingType);
        const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign);
        return state[rankingStoreName][storageKey];
    },
};

const actions = {
    clean({ commit }) {
        commit(CLEAN_REPORTS_CATEGORY);
    },

    async fetchImpressionsChart({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate) {
                return;
            }

            campaign.sort();
            category.sort();
            const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
            if (!force && state.impressionsChart && state.impressionsChart[storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_IMPRESSIONS_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;
            const responseData = await reportsCategoryService.fetchImpressionsReport(spaceId, startDate, endDate, reqCampaign, reqCategory);
            const { chart, chartCategories } = parseChartData(responseData, startDate, endDate);

            commit(FETCH_REPORTS_CATEGORY_IMPRESSIONS_SUCCESS, {
                chart,
                chartCategories,
                pieChart: responseData.pie_chart,
                storageKey,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_IMPRESSIONS_FAILURE);
            throw e;
        }
    },

    async fetchClicksChart({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate) {
                return;
            }

            campaign.sort();
            category.sort();
            const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
            if (!force && state.clicksChart && state.clicksChart[storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_CLICKS_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;
            const responseData = await reportsCategoryService.fetchClicksReport(spaceId, startDate, endDate, reqCampaign, reqCategory);
            const { chart, chartCategories } = parseChartData(responseData, startDate, endDate);

            commit(FETCH_REPORTS_CATEGORY_CLICKS_SUCCESS, {
                chart,
                chartCategories,
                pieChart: responseData.pie_chart,
                storageKey,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_CLICKS_FAILURE);
            throw e;
        }
    },

    async fetchQuantityChart({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate) {
                return;
            }

            campaign.sort();
            category.sort();
            const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
            if (!force && state.quantityChart && state.quantityChart[storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_QUANTITY_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;
            const responseData = await reportsCategoryService.fetchQuantityReport(spaceId, startDate, endDate, reqCampaign, reqCategory);
            const { chart, chartCategories } = parseChartData(responseData, startDate, endDate);

            commit(FETCH_REPORTS_CATEGORY_QUANTITY_SUCCESS, {
                chart,
                chartCategories,
                pieChart: responseData.pie_chart,
                storageKey,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_QUANTITY_FAILURE);
            throw e;
        }
    },

    async fetchAmountChart({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate) {
                return;
            }

            campaign.sort();
            category.sort();
            const storageKey = utilsService.getStorageKey(startDate, endDate, ...campaign, ...category);
            if (!force && state.amountChart && state.amountChart[storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_AMOUNT_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;
            const responseData = await reportsCategoryService.fetchAmountReport(spaceId, startDate, endDate, reqCampaign, reqCategory);
            const { chart, chartCategories } = parseChartData(responseData, startDate, endDate);

            commit(FETCH_REPORTS_CATEGORY_AMOUNT_SUCCESS, {
                chart,
                chartCategories,
                pieChart: responseData.pie_chart,
                storageKey,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_AMOUNT_FAILURE);
            throw e;
        }
    },

    async fetchHeatmap({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, dataType,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate || !dataType) {
                return;
            }

            const heatmapStoreName = getHeatmapStoreName(dataType);
            const storageKey = utilsService.getStorageKey(startDate, endDate);
            if (!force && state[heatmapStoreName] && state[heatmapStoreName][storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_HEATMAP_REQUEST);

            let heatmapData;
            switch (dataType) {
                case 'impressions':
                    heatmapData = await reportsCategoryService.fetchImpressionsHeatmap(spaceId, startDate, endDate);
                    break;
                case 'clicks':
                    heatmapData = await reportsCategoryService.fetchClicksHeatmap(spaceId, startDate, endDate);
                    break;
                case 'quantity':
                    heatmapData = await reportsCategoryService.fetchQuantityHeatmap(spaceId, startDate, endDate);
                    break;
                case 'amount':
                    heatmapData = await reportsCategoryService.fetchAmountHeatmap(spaceId, startDate, endDate);
                    break;
                default:
                    break;
            }

            let heatmap = [];
            let categories = [];

            if (heatmapData && heatmapData.length > 0) {
                heatmap = heatmapData.slice(1);
                categories = heatmapData[0].slice(1);
            }

            commit(FETCH_REPORTS_CATEGORY_HEATMAP_SUCCESS, {
                heatmap,
                categories,
                startDate,
                endDate,
                dataType,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_HEATMAP_FAILURE);
            throw e;
        }
    },

    async fetchSummary({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, compareStartDate, compareEndDate, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate) {
                return;
            }

            campaign.sort();
            category.sort();
            const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign, ...category);
            if (!force && state.summary && state.summary[storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_SUMMARY_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;
            const summaryData = await reportsCategoryService.fetchSummary(spaceId, startDate, endDate, compareStartDate, compareEndDate, reqCampaign, reqCategory);

            commit(FETCH_REPORTS_CATEGORY_SUMMARY_SUCCESS, {
                summaryData,
                startDate,
                endDate,
                compareStartDate,
                compareEndDate,
                campaign,
                category,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_SUMMARY_FAILURE);
            throw e;
        }
    },

    async fetchRanking({ commit, state, rootState }, payload = {}) {
        try {
            const {
                force, startDate, endDate, compareStartDate, compareEndDate, rankingType, campaign, category,
            } = payload;
            const spaceId = rootState.space.currentSpaceId;

            if (!spaceId || !startDate || !endDate || !compareStartDate || !compareEndDate || !rankingType) {
                return;
            }

            campaign.sort();
            category.sort();
            const rankingStoreName = getRankingStoreName(rankingType);
            const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign, ...category);
            if (!force && state[rankingStoreName] && state[rankingStoreName][storageKey]) {
                return;
            }

            commit(FETCH_REPORTS_CATEGORY_RANKING_REQUEST);
            const reqCampaign = campaign.length > 0 ? campaign.join(',') : null;
            const reqCategory = category.length > 0 ? category.join(',') : null;

            let rankingData;
            switch (rankingType) {
                case 'impressions':
                    rankingData = await reportsCategoryService.fetchImpressionsRanking(spaceId, startDate, endDate, compareStartDate, compareEndDate, reqCampaign, reqCategory);
                    break;
                case 'clicks':
                    rankingData = await reportsCategoryService.fetchClicksRanking(spaceId, startDate, endDate, compareStartDate, compareEndDate, reqCampaign, reqCategory);
                    break;
                case 'quantity':
                    rankingData = await reportsCategoryService.fetchQuantityRanking(spaceId, startDate, endDate, compareStartDate, compareEndDate, reqCampaign, reqCategory);
                    break;
                case 'amount':
                    rankingData = await reportsCategoryService.fetchAmountRanking(spaceId, startDate, endDate, compareStartDate, compareEndDate, reqCampaign, reqCategory);
                    break;
                default:
                    break;
            }

            commit(FETCH_REPORTS_CATEGORY_RANKING_SUCCESS, {
                rankingData,
                startDate,
                endDate,
                compareStartDate,
                compareEndDate,
                rankingType,
                campaign,
                category,
            });
        } catch (e) {
            commit(FETCH_REPORTS_CATEGORY_RANKING_FAILURE);
            throw e;
        }
    },
};

const mutations = {
    // -----------------------------------------
    // CLEAN_REPORTS_CATEGORY
    // -----------------------------------------
    [CLEAN_REPORTS_CATEGORY](state) {
        state.impressionsChart = {};
        state.clicksChart = {};
        state.quantityChart = {};
        state.amountChart = {};
        state.heatmapImpressions = {};
        state.heatmapClicks = {};
        state.heatmapQuantity = {};
        state.heatmapAmount = {};
        state.loading.impressionsChart = false;
        state.loading.clicksChart = false;
        state.loading.quantityChart = false;
        state.loading.amountChart = false;
        state.loading.heatmap = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_IMPRESSIONS
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_IMPRESSIONS_REQUEST](state) {
        state.loading.impressionsChart = true;
    },
    [FETCH_REPORTS_CATEGORY_IMPRESSIONS_SUCCESS](state, payload) {
        const {
            chart, chartCategories, pieChart, storageKey,
        } = payload;

        state.loading.impressionsChart = false;
        state.impressionsChart = {
            ...state.impressionsChart,
            [storageKey]: {
                chart,
                chartCategories,
                pieChart,
            },
        };
    },
    [FETCH_REPORTS_CATEGORY_IMPRESSIONS_FAILURE](state) {
        state.loading.impressionsChart = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_CLICKS
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_CLICKS_REQUEST](state) {
        state.loading.clicksChart = true;
    },
    [FETCH_REPORTS_CATEGORY_CLICKS_SUCCESS](state, payload) {
        const {
            chart, chartCategories, pieChart, storageKey,
        } = payload;

        state.loading.clicksChart = false;
        state.clicksChart = {
            ...state.clicksChart,
            [storageKey]: {
                chart,
                chartCategories,
                pieChart,
            },
        };
    },
    [FETCH_REPORTS_CATEGORY_CLICKS_FAILURE](state) {
        state.loading.clicksChart = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_QUANTITY
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_QUANTITY_REQUEST](state) {
        state.loading.quantityChart = true;
    },
    [FETCH_REPORTS_CATEGORY_QUANTITY_SUCCESS](state, payload) {
        const {
            chart, chartCategories, pieChart, storageKey,
        } = payload;

        state.loading.quantityChart = false;
        state.quantityChart = {
            ...state.quantityChart,
            [storageKey]: {
                chart,
                chartCategories,
                pieChart,
            },
        };
    },
    [FETCH_REPORTS_CATEGORY_QUANTITY_FAILURE](state) {
        state.loading.quantityChart = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_AMOUNT
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_AMOUNT_REQUEST](state) {
        state.loading.amountChart = true;
    },
    [FETCH_REPORTS_CATEGORY_AMOUNT_SUCCESS](state, payload) {
        const {
            chart, chartCategories, pieChart, storageKey,
        } = payload;

        state.loading.amountChart = false;
        state.amountChart = {
            ...state.amountChart,
            [storageKey]: {
                chart,
                chartCategories,
                pieChart,
            },
        };
    },
    [FETCH_REPORTS_CATEGORY_AMOUNT_FAILURE](state) {
        state.loading.amountChart = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_HEATMAP
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_HEATMAP_REQUEST](state) {
        state.loading.heatmap = true;
    },
    [FETCH_REPORTS_CATEGORY_HEATMAP_SUCCESS](state, payload) {
        const {
            heatmap, categories, startDate, endDate, dataType,
        } = payload;
        const heatmapStoreName = getHeatmapStoreName(dataType);
        const storageKey = utilsService.getStorageKey(startDate, endDate);

        state.loading.heatmap = false;
        state[heatmapStoreName] = {
            ...state[heatmapStoreName],
            [storageKey]: {
                heatmap,
                categories,
            },
        };
    },
    [FETCH_REPORTS_CATEGORY_HEATMAP_FAILURE](state) {
        state.loading.heatmap = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_SUMMARY
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_SUMMARY_REQUEST](state) {
        state.loading.summary = true;
    },
    [FETCH_REPORTS_CATEGORY_SUMMARY_SUCCESS](state, payload) {
        const {
            summaryData, startDate, endDate, compareStartDate, compareEndDate, campaign, category,
        } = payload;
        const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign, ...category);
        const summaryParsed = summaryData.map(item => {
            const impressionsChange = numberService.calculateChangePercentage(item.impressions, item.compareImpressions);
            const clicksChange = numberService.calculateChangePercentage(item.clicks, item.compareClicks);
            const quantityChange = numberService.calculateChangePercentage(item.quantity, item.compareQuantity);
            const amountChange = numberService.calculateChangePercentage(item.amount, item.compareAmount);

            return {
                ...item,
                impressionsChange,
                clicksChange,
                quantityChange,
                amountChange,
            };
        });

        state.loading.summary = false;
        state.summary = {
            ...state.summary,
            [storageKey]: summaryParsed,
        };
    },
    [FETCH_REPORTS_CATEGORY_SUMMARY_FAILURE](state) {
        state.loading.summary = false;
    },

    // -----------------------------------------
    // FETCH_REPORTS_CATEGORY_RANKING
    // -----------------------------------------
    [FETCH_REPORTS_CATEGORY_RANKING_REQUEST](state) {
        state.loading.ranking = true;
    },
    [FETCH_REPORTS_CATEGORY_RANKING_SUCCESS](state, payload) {
        const {
            rankingData, startDate, endDate, compareStartDate, compareEndDate, rankingType, campaign,
        } = payload;
        const rankingStoreName = getRankingStoreName(rankingType);
        const storageKey = utilsService.getStorageKey(startDate, endDate, compareStartDate, compareEndDate, ...campaign);
        const parsedRankingData = rankingData.map(item => {
            const summary = parseFloat(item.summary);
            const summaryComparison = parseFloat(item.summaryComparison);
            const summaryChange = numberService.calculateChangePercentage(summary, summaryComparison);

            return {
                ...item,
                summary: Number.isNaN(summary) ? null : summary,
                summaryComparison: Number.isNaN(summaryComparison) ? null : summaryComparison,
                summaryChange,
            };
        });

        state.loading.ranking = false;
        state[rankingStoreName] = {
            ...state[rankingStoreName],
            [storageKey]: parsedRankingData,
        };
    },
    [FETCH_REPORTS_CATEGORY_RANKING_FAILURE](state) {
        state.loading.ranking = false;
    },
};

export default {
    namespaced: true,
    state: initialState,
    getters,
    actions,
    mutations,
};
